import { useState, useEffect, useRef, useCallback } from 'react';
import type { TokenData } from '../types';

interface RelayPriceData {
  tokenAddress: string;
  symbol: string;
  priceUSD: number;
  timestamp: number;
}

interface RelayPriceResponse {
  price: number;
}


const CACHE_DURATION = 30000; // 30 seconds cache
const RELAY_API_BASE = 'https://api.relay.link';
const INK_CHAIN_ID = 57073;
const MONAD_CHAIN_ID = 143;
const ETH_CHAIN_ID = 1;
const SOLANA_CHAIN_ID = 792703809; // Relay API Solana chain ID

// Cache management
let priceCache: Map<string, { price: number; timestamp: number }> = new Map();

/**
 * Fetch token price from Relay API (for EVM chains)
 */
const fetchRelayPrice = async (tokenAddress: string, chainId: number = INK_CHAIN_ID): Promise<number | null> => {
  try {
    // Check cache first (include chainId in cache key)
    const cacheKey = `${tokenAddress.toLowerCase()}_${chainId}`;
    const cached = priceCache.get(cacheKey);
    if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
      console.log(`[Relay] Using cached price for ${tokenAddress} on chain ${chainId}: $${cached.price}`);
      return cached.price;
    }

    const url = `${RELAY_API_BASE}/currencies/token/price?address=${tokenAddress}&chainId=${chainId}&referrer=mavrk.ink`;

    console.log(`[Relay] Fetching price for ${tokenAddress} on chain ${chainId}`);
    const response = await fetch(url);

    if (!response.ok) {
      console.warn(`[Relay] API returned ${response.status} for ${tokenAddress} on chain ${chainId}`);
      return null;
    }

    const data: RelayPriceResponse = await response.json();

    if (typeof data.price === 'number' && data.price > 0) {
      // Update cache with chainId in key
      priceCache.set(cacheKey, {
        price: data.price,
        timestamp: Date.now()
      });

      console.log(`[Relay] Fetched price for ${tokenAddress} on chain ${chainId}: $${data.price}`);
      return data.price;
    }

    return null;
  } catch (error) {
    console.error(`[Relay] Error fetching price for ${tokenAddress} on chain ${chainId}:`, error);
    return null;
  }
};


/**
 * Batch fetch prices for multiple tokens with rate limiting
 * Uses Relay API for all chains including Solana (chainId 792703809)
 */
const fetchRelayPricesBatch = async (tokens: TokenData[]): Promise<RelayPriceData[]> => {
  const results: RelayPriceData[] = [];

  // Filter to tokens with addresses
  const supportedTokens = tokens.filter(t => t.contract_address || (t as any).mint_address);

  if (supportedTokens.length === 0) {
    return results;
  }

  console.log(`[Relay] Fetching prices for ${supportedTokens.length} tokens`);

  const CHUNK_SIZE = 10;
  const CHUNK_DELAY = 100;

  for (let i = 0; i < supportedTokens.length; i += CHUNK_SIZE) {
    const chunk = supportedTokens.slice(i, i + CHUNK_SIZE);

    const chunkPromises = chunk.map(async (token) => {
      // Determine chain ID based on network
      let chainId = INK_CHAIN_ID;
      if (token.network === 'monad') chainId = MONAD_CHAIN_ID;
      else if (token.network === 'eth') chainId = ETH_CHAIN_ID;
      else if (token.network === 'sol' || token.network === 'solana') chainId = SOLANA_CHAIN_ID;

      // Use mint_address for Solana, contract_address for others
      const address = (token as any).mint_address || token.contract_address;
      const price = await fetchRelayPrice(address, chainId);

      if (price !== null) {
        return {
          tokenAddress: address,
          symbol: token.symbol || 'UNKNOWN',
          priceUSD: price,
          timestamp: Date.now()
        };
      }
      return null;
    });

    const chunkResults = await Promise.all(chunkPromises);
    results.push(...chunkResults.filter((r): r is RelayPriceData => r !== null));

    if (i + CHUNK_SIZE < supportedTokens.length) {
      await new Promise(resolve => setTimeout(resolve, CHUNK_DELAY));
    }
  }

  console.log(`[Relay] Successfully fetched ${results.length}/${supportedTokens.length} token prices`);
  return results;
};

/**
 * React hook to fetch token prices from Relay API
 * 
 * This replaces the complex slot0 pricing logic with a simple API call
 * Benefits:
 * - No RPC calls (reduces rate limiting)
 * - Direct USD pricing (no ETH conversion needed)
 * - Simpler, faster, more reliable
 */
export const useRelayPrice = (tokens: TokenData[]) => {
  const [prices, setPrices] = useState<RelayPriceData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const fetchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isFetchingRef = useRef<boolean>(false);

  // Create stable token identifier to prevent infinite loops
  const tokenAddresses = tokens
    .filter(t => t.contract_address)
    .map(t => t.contract_address)
    .sort()
    .join(',');

  const fetchPrices = useCallback(async (forceRefresh = false) => {
    // Prevent duplicate concurrent fetches for the same component
    if (!forceRefresh && isFetchingRef.current) {
      return; // Removed console.log to reduce noise
    }

    isFetchingRef.current = true;
    setLoading(true);
    setError(null);

    try {
      const priceData = await fetchRelayPricesBatch(tokens);
      setPrices(priceData);
      setError(null);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error fetching prices';
      console.error('[Relay] Error fetching prices:', err);
      setError(errorMessage);
    } finally {
      setLoading(false);
      isFetchingRef.current = false;
    }
  }, [tokens]); // Depend on tokens array

  useEffect(() => {
    // Clear any pending timeout
    if (fetchTimeoutRef.current) {
      clearInterval(fetchTimeoutRef.current);
      fetchTimeoutRef.current = null;
    }

    // Only fetch if we have tokens
    if (tokens.length === 0) {
      setPrices([]);
      setLoading(false);
      return;
    }

    // Debounce initial fetch to prevent rapid successive calls
    const initialFetchTimeout = setTimeout(() => {
      fetchPrices();
    }, 300); // 300ms debounce

    // Set up auto-refresh every 30 seconds (only if component is still mounted)
    fetchTimeoutRef.current = setInterval(() => {
      if (!isFetchingRef.current) {
        fetchPrices(true); // Force refresh on interval
      }
    }, 30000);

    return () => {
      clearTimeout(initialFetchTimeout);
      if (fetchTimeoutRef.current) {
        clearInterval(fetchTimeoutRef.current);
        fetchTimeoutRef.current = null;
      }
      isFetchingRef.current = false; // Reset on unmount
    };
  }, [tokenAddresses, fetchPrices]); // Include fetchPrices in deps

  // Helper function to get price for a specific token
  const getPriceForToken = useCallback((tokenAddress: string): number | null => {
    const priceData = prices.find(
      p => p.tokenAddress.toLowerCase() === tokenAddress.toLowerCase()
    );
    return priceData ? priceData.priceUSD : null;
  }, [prices]);

  return {
    prices,
    loading,
    error,
    refetch: () => fetchPrices(true),
    getPriceForToken
  };
};

/**
 * Standalone function to fetch a single token price
 * Useful for one-off price checks
 * @param tokenAddress - The token contract address
 * @param chainId - Optional chain ID (defaults to INK chain 57073)
 */
export const getRelayTokenPrice = async (tokenAddress: string, chainId: number = INK_CHAIN_ID): Promise<number | null> => {
  return fetchRelayPrice(tokenAddress, chainId);
};

/**
 * Fetch token price on Ethereum mainnet
 */
export const getRelayTokenPriceEth = async (tokenAddress: string): Promise<number | null> => {
  return fetchRelayPrice(tokenAddress, ETH_CHAIN_ID);
};

/**
 * Fetch token price on Solana using Relay API
 * @param mintAddress - Solana SPL token mint address
 */
export const getRelayTokenPriceSolana = async (mintAddress: string): Promise<number | null> => {
  return fetchRelayPrice(mintAddress, SOLANA_CHAIN_ID);
};

/**
 * Clear the price cache (useful for testing or forced refresh)
 */
export const clearRelayPriceCache = () => {
  priceCache.clear();
  console.log('[Relay] Price cache cleared');
};

